import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { LabelColor } from '@pflegenavi/web-components';
import { Label, makeConfig, PaginatedTable2 } from '@pflegenavi/web-components';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import type { Expense } from '@pflegenavi/frontend/api-nursing-home';
import { useExpensesByCashGroup } from '@pflegenavi/frontend/api-nursing-home';
import {
  EXPENSE_ID_SEARCH_PARAM,
  nhAppMainPages,
} from '@pflegenavi/frontend/routing';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import { LinkedTableSection } from './LinkedTableSection';

type ColumnNames =
  | 'receiptDate'
  | 'merchant'
  | 'receiptNumber'
  | 'amount'
  | 'status';

type ExpenseStatus = 'open' | 'scheduled' | 'exported';

const expenseStatus = (expense: Expense): ExpenseStatus =>
  expense.expense_export_id
    ? 'exported'
    : expense.scheduled_for_export_at
    ? 'scheduled'
    : 'open';

const STATUS: Record<ExpenseStatus, { color: LabelColor; path: string }> = {
  open: { color: 'warning', path: nhAppMainPages.EXPENSE_MANAGEMENT_OPEN },
  scheduled: {
    color: 'info',
    path: nhAppMainPages.EXPENSE_MANAGEMENT_SCHEDULED,
  },
  exported: {
    color: 'success',
    path: nhAppMainPages.EXPENSE_MANAGEMENT_EXPORTED,
  },
};

/** A row opens the expense in the expense management tab it currently sits in. */
export const ExpenseTable: FC<{ groupId: string }> = ({ groupId }) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const navigate = useNavigate();

  const { data, isLoading } = useExpensesByCashGroup(groupId);

  const config = useMemo(
    () =>
      makeConfig<Expense, ColumnNames, never>({
        showAll: undefined,
        hideEmptyRows: true,
        onClickRow: (row) =>
          navigate({
            pathname: STATUS[expenseStatus(row)].path,
            search: new URLSearchParams({
              [EXPENSE_ID_SEARCH_PARAM]: row.id,
            }).toString(),
          }),
        columns: [
          {
            key: 'receiptDate',
            label: t('accounting.expenses.table.receipt-date'),
            align: 'left',
            component: ({ row }) =>
              row.receipt_date
                ? fDate(new Date(row.receipt_date), YEAR_MONTH_DAY_SHORT_FORMAT)
                : '--',
          },
          {
            key: 'merchant',
            label: t('accounting.expenses.table.merchant'),
            align: 'left',
            component: ({ row }) => row.merchant_name ?? '--',
          },
          {
            key: 'receiptNumber',
            label: t('accounting.expenses.table.receipt'),
            align: 'left',
            component: ({ row }) => row.receipt_number ?? '--',
          },
          {
            key: 'amount',
            label: t('accounting.expenses.table.amount'),
            align: 'right',
            component: ({ row }) => (
              <Typography variant="subtitle2" noWrap>
                {fCurrencyCents(
                  row.expense_parts.reduce((sum, part) => sum + part.amount, 0)
                )}
              </Typography>
            ),
          },
          {
            key: 'status',
            label: t('accounting.expenses.table.status'),
            align: 'left',
            component: ({ row }) => {
              const status = expenseStatus(row);
              return (
                <Label color={STATUS[status].color} variant="ghost">
                  {t(`cashManagement.group-detail.expense-status.${status}`)}
                </Label>
              );
            },
          },
        ],
      }),
    [t, fDate, fCurrencyCents, navigate]
  );

  return (
    <LinkedTableSection
      title={t('cashManagement.group-detail.overview.linked-expense-title')}
      count={data?.data.length}
    >
      <PaginatedTable2
        name="CashTransactionGroupDetailExpenseTable"
        config={config}
        rows={data?.data ?? []}
        isLoading={isLoading}
      />
    </LinkedTableSection>
  );
};
